import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '@environments';

const GTM_SCRIPT_ID = 'gtm-script';

interface GtmEvent {
  event: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class GtmService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private initialized = false;


  initialize(): void {
    if (this.initialized || !environment.GTM_ID) {
      return;
    }

    this.injectScript(environment.GTM_ID);
    this.trackPageViews();
    this.initialized = true;
  }


  pushEvent(event: GtmEvent): void {
    const dataLayer = this.getDataLayer();
    dataLayer.push(event);
  }


  trackStep(stepName: string, stepNumber: number): void {
    this.pushEvent({
      event: 'onboarding',
      eAction: `paso ${stepNumber}`,
      eLabel: stepName,
      pageName: this.router.url
    });
  }

  private injectScript(gtmId: string): void {
    const doc = this.document;

    if (doc.getElementById(GTM_SCRIPT_ID)) {
      return;
    }

    const win = doc.defaultView as unknown as Window & { dataLayer: unknown[] };
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    const script = doc.createElement('script');
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    doc.head.appendChild(script);

    const noscript = doc.createElement('noscript');
    const iframe = doc.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    doc.body.insertBefore(noscript, doc.body.firstChild);
  }

  private trackPageViews(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.pushEvent({
        event: 'page_view',
        pagePath: event.urlAfterRedirects
      });
    });
  }

  private getDataLayer(): unknown[] {
    const win = this.document.defaultView as unknown as Window & { dataLayer: unknown[] };
    if (!win) {
      return [];
    }
    if (!win.dataLayer) {
      win.dataLayer = [];
    }
    return win.dataLayer;
  }
}
